import { useSubscription } from '@/shared/api/queries'
import { useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import { Badge, Card, KeyValue, Skeleton, Stack, StateBlock, Text } from '@/shared/ui'

const UNLIMITED = -1

export function SubscriptionPage() {
  const t = useTranslate()
  const { data, error, isPending, refetch } = useSubscription()

  const premium = data?.plan === 'premium'

  const plan: TranslationKey = premium ? 'subscription.plan.premium' : 'subscription.plan.free'

  function limit(value: number | undefined): string {
    if (value === undefined) {
      return '—'
    }

    return value === UNLIMITED ? t('subscription.unlimited') : String(value)
  }

  return (
    <Stack gap="md" className="page--detail">
      <Text as="h1" role="heading-2">
        {t('subscription.title')}
      </Text>

      <StateBlock
        pending={isPending}
        error={error}
        onRetry={() => void refetch()}
        skeleton={<Skeleton variant="card" rows={2} />}
      >
        <Stack gap="md">
          <Card inverse>
            <Stack direction="row" justify="between" align="start" gap="sm">
              <Text role="eyebrow" tone="muted">
                {t('subscription.title')}
              </Text>
              {data?.period_to ? (
                <Badge tone>{t('subscription.until', { date: data.period_to })}</Badge>
              ) : null}
            </Stack>
            <Text role="date">{t(plan)}</Text>
          </Card>

          <Card>
            <KeyValue label={t('subscription.limit.documents')}>
              {limit(data?.limits.documents)}
            </KeyValue>
            <KeyValue label={t('subscription.limit.ai')}>
              {limit(data?.limits.ai_requests_per_day)}
            </KeyValue>
          </Card>

          {!premium ? (
            <Card flat className="card--dashed">
              <Text role="body-strong">{t('subscription.plan.premium')}</Text>
              <Text role="caption" tone="muted" measure>
                {t('subscription.upgrade.body')}
              </Text>
            </Card>
          ) : null}
        </Stack>
      </StateBlock>
    </Stack>
  )
}
